import { useNovel } from '@/context/novel-context';
import { useTranslation } from 'react-i18next';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Star as StarIcon } from '@mui/icons-material';

import { CollaborationListCollaborators } from './collaboration-list-collaborators';
import { CollaborationListReaders } from './collaboration-list-readers';

type CollaborationMemberListProps = {
  currentUserUid?: string;
  currentUserEmail?: string | null;
  isCurrentUserOwner: boolean;
  isUpdatingCollab: boolean;
  isUpdatingReader: boolean;
  updatingCollabMemberId: string | null;
  updatingReaderMemberId: string | null;
  onRemoveCollaborator: (id: string) => Promise<void>;
  onRemoveReader: (id: string) => Promise<void>;
  onRemovePendingCollaborator: (email: string) => Promise<void>;
  onRemovePendingReader: (email: string) => Promise<void>;
};

export function CollaborationMemberList({
  currentUserUid,
  currentUserEmail,
  isCurrentUserOwner,
  isUpdatingCollab,
  isUpdatingReader,
  updatingCollabMemberId,
  updatingReaderMemberId,
  onRemoveCollaborator,
  onRemoveReader,
  onRemovePendingCollaborator,
  onRemovePendingReader,
}: CollaborationMemberListProps) {
  const { t } = useTranslation();
  const { novel } = useNovel();
  if (!novel.id) return null;

  const ownerName = novel.ownerId === currentUserUid
    ? `${t('dashboard.settings.you')}`
    : `Owner (${novel.ownerId})`;
  const ownerSub = novel.ownerId === currentUserUid
    ? currentUserEmail
    : null;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
          {t('dashboard.settings.owner')}
        </Typography>
        <List disablePadding>
          <ListItem
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`, color: 'primary.contrastText' }}>
                <StarIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {ownerName}
                  </Typography>
                  <Chip
                    label={t('dashboard.settings.owner_badge')}
                    size="small"
                    sx={{
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                      color: 'primary.contrastText',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      border: 'none',
                      height: 20
                    }}
                  />
                </Stack>
              }
              secondary={ownerSub}
            />
          </ListItem>
        </List>
      </Box>

      <Divider />

      <CollaborationListCollaborators
        currentUserUid={currentUserUid}
        isCurrentUserOwner={isCurrentUserOwner}
        isUpdatingCollab={isUpdatingCollab}
        updatingMemberId={updatingCollabMemberId}
        onRemoveCollaborator={onRemoveCollaborator}
        onRemovePendingCollaborator={onRemovePendingCollaborator}
      />

      <Divider />

      <CollaborationListReaders
        currentUserUid={currentUserUid}
        isCurrentUserOwner={isCurrentUserOwner}
        isUpdatingReader={isUpdatingReader}
        updatingMemberId={updatingReaderMemberId}
        onRemoveReader={onRemoveReader}
        onRemovePendingReader={onRemovePendingReader}
      />
    </Stack>
  );
}
